import {
  Box,
  FormField,
  Input,
  SpaceBetween,
  Header,
  Spinner,
  Alert,
} from "@cloudscape-design/components";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import organizationService from "../services/organizationService";
import SubmitButton from "styles/SubmitButton";
import { useBreadcrumbs } from "../contexts/BreadcrumbContext";
import { useNotification } from "contexts/NotificationContext";
import { Divider } from "@mui/material";
import UserService from "services/UserService";
import PERMISSIONS from "configs/Permissions";
import AppConfig from "configs/AppConfig";

export default function OrganizationSettings() {
  const { addNotification } = useNotification();
  const { setBreadcrumbs } = useBreadcrumbs();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [organization, setOrganization] = useState(
    location.state?.organization || null
  );
  const [displayName, setDisplayName] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);


  const fetchOrganizationData = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const orgData = await organizationService.getOrganizationById(id);
      setOrganization(orgData);
      setLoading(false);
      setError(null);
    } catch (error) {
      console.error("Error fetching organization:", error);
      setError("Failed to load organization details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!organization && id) {
      fetchOrganizationData();
    } else if (organization) {
      setLoading(false);
      setDisplayName(organization.display_name);
    }
  }, [organization, id]);

  useEffect(() => {
    if (organization) {
      setBreadcrumbs([
        { text: AppConfig.APP_NAME, href: "/" },
        { text: "Organizations", href: "/organizations" },
        { text: organization.display_name, href: `/organizations/${id}` },
        { text: "Settings", href: `/organizations/${id}/settings` },
      ]);
    }

    // Cleanup when component unmounts
    return () => {
      setBreadcrumbs([]);
    };
  }, [organization, id, setBreadcrumbs]);

  const updateDisplayName = async () => {
    if (!id || !displayName.trim() || displayName.length > 50) return;

    try {
      await organizationService.updateOrganization(id, {
        attribute:"display_name",
        value:displayName
      });
      addNotification(
        "success",
        "Display name updated successfully"
      );
      navigate(`/organizations/${id}`);
    } catch (error) {
      addNotification("error", "Failed to update organization Display Name.");
    }
  };

  const handleDomainSetupClick = () => {
    if (organization) {
      navigate(`/organizations/${id}/domains`, {
        state: { organization },
      });
    }
  };
  const handleSSOSetupClick = () => {
    if (organization) {
      navigate(`/organizations/${id}/sso-providers`, {
        state: { organization },
      });
    }
  };


  return (
    <SpaceBetween size="l">
      {loading && (
        <Box padding={{ horizontal: "m", vertical: "l" }}>
          <Spinner size="large" />
          <Box color="text-status-info" fontSize="heading-s">
            Loading organization settings...
          </Box>
        </Box>
      )}

      {!loading && (error || !organization) && (
        <Box padding={{ horizontal: "m", vertical: "l" }}>
          <Alert type="error">{error || "Organization not found."}</Alert>
        </Box>
      )}
      {!loading && !error && organization && (
        <>
          <Box margin={{ bottom: "l" }}>
            <Header variant="h2">Settings</Header>

          </Box>
          <Header variant="h2">General</Header>
          <SpaceBetween size="l">
            <FormField label="ID">
              <Input value={id ?? ""} disabled />
            </FormField>

            <FormField label="Name">
              <Input value={organization.name} disabled />
            </FormField>

            <FormField
              label="Display Name"
              errorText={
                !displayName.trim()
                  ? "Display name cannot be empty"
                  : displayName.length > 50
                  ? "Display name cannot exceed 50 characters"
                  : undefined
              }
              constraintText={
                <>
                  Display name must be 1 to 50 characters. Character count:{" "}
                  {displayName.length}/50
                </>
              }
            >
              <Input
                value={displayName}
                onChange={({ detail }) => setDisplayName(detail.value)}
                disabled={!UserService.hasRole([PERMISSIONS.ORGANIZATIONS.MANAGE_ORGANIZATIONS])}
              />
            </FormField>

            <Box textAlign="right">
              <div className="update-button">
                <SubmitButton
                  variant="primary"
                  onClick={updateDisplayName}
                  disabled={
                    !UserService.hasRole([PERMISSIONS.ORGANIZATIONS.MANAGE_ORGANIZATIONS]) ||
                    !displayName.trim() ||
                    displayName === organization.display_name ||
                    displayName.length > 50 
                  }
                >
                  Update organization
                </SubmitButton>
              </div>
            </Box>
          </SpaceBetween>

          <Divider />

          <SpaceBetween size="xs">
            <Header variant="h2">Domains</Header>
            <Box variant="p">View linked domains and verify DNS entries</Box>
          </SpaceBetween>
          <SpaceBetween size="l">
            <Box>
              <div className="add-button">
                <SubmitButton
                  variant="primary"
                  onClick={handleDomainSetupClick}
                  disabled={!UserService.hasRole([PERMISSIONS.ORGANIZATIONS.MANAGE_ORGANIZATIONS])}
                >
                  View domains
                </SubmitButton>
              </div>
            </Box>
          </SpaceBetween>
          <Divider />
          <SpaceBetween size="xs">
            <Header variant="h2">SSO</Header>
            <Box variant="p">Add an SSO provider</Box>
          </SpaceBetween>
          <SpaceBetween size="l">
            <Box>
              <div className="add-button">
                <SubmitButton variant="primary" onClick={handleSSOSetupClick} disabled={!UserService.hasRole([PERMISSIONS.ORGANIZATIONS.MANAGE_ORGANIZATIONS])}>
                  Setup SSO
                </SubmitButton>
              </div>
            </Box>
          </SpaceBetween>
        </>
      )}
    </SpaceBetween>
  );
}
